import React, { useState, useEffect } from 'react';
import authService from '../services/authService';
import { buildApiUrl } from '../config';

const UserManagement = () => {
  const [activeTab, setActiveTab] = useState('staff');
  const [staffData, setStaffData] = useState([]);
  const [securityGroups, setSecurityGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedSchool, setSelectedSchool] = useState('all'); // Add school filtering state

  // Get current user info for school filtering
  const currentUser = authService.getCurrentUser();

  // 🏫 SIMPLE SCHOOL ACCESS CONFIGURATION
  // Add users here who should have specific school access
  const getSchoolAccessForUser = (email) => {
    // First check localStorage for dynamic assignments
    const schoolAssignments = JSON.parse(localStorage.getItem('schoolAssignments') || '{}');
    if (schoolAssignments[email]) {
      return schoolAssignments[email];
    }
    
    // Then check static configuration
    const accessConfig = {
      // Examples - add more users as needed:
      'amghani@sherborneqatar.org': { school: 'Boys School', canManage: false },
      // 'headmaster.girls@sherborneqatar.org': { school: 'Girls School', canManage: false },
      // 'headmaster.bh@sherborneqatar.org': { school: 'BaniHajer School', canManage: false },
      // 'headmaster.moq@sherborneqatar.org': { school: 'MOQ School', canManage: false },
      
      // Super admin gets all access by default
      'hshehadeh@sherborneqatar.org': { school: null, canManage: true },
    };
    
    return accessConfig[email] || null;
  };

  // School filter options based on user permissions (same as HR page)
  const getAllSchoolOptions = () => [
    { value: 'all', label: 'All Schools', departments: [] },
    { value: 'boys', label: 'Boys School', departments: ['BS Teacher', 'BS Bursary', 'BS TA'] },
    { value: 'girls', label: 'Girls School', departments: ['GS Bursary', 'GS Teacher', 'GS TA'] },
    { value: 'banihajer', label: 'BaniHajer School', departments: ['BH Bursary', 'BH Teacher', 'BH TA'] },
    { value: 'moq', label: 'MOQ School', departments: ['MOQ Teacher', 'MOQ Bursary'] }
  ];

  const getFilteredSchoolOptions = () => {
    const allOptions = getAllSchoolOptions();
    
    // Check if user has specific school access configuration
    const userAccess = getSchoolAccessForUser(currentUser?.email);
    if (userAccess) {
      if (userAccess.canManage) {
        // User can manage all schools (like super admin)
        return allOptions;
      } else if (userAccess.school) {
        // User has specific school access
        const schoolMapping = {
          'Boys School': 'boys',
          'Girls School': 'girls', 
          'BaniHajer School': 'banihajer',
          'MOQ School': 'moq'
        };
        
        const userSchoolKey = schoolMapping[userAccess.school];
        if (userSchoolKey) {
          return allOptions.filter(option => option.value === userSchoolKey);
        }
      }
    }
    
    // Fallback to original logic for users not in configuration
    // If user can manage HR or has view-all-schools permission, show all options
    if (authService.canManageHR() || currentUser?.hrPermissions?.includes('view-all-schools')) {
      return allOptions;
    }
    
    // For regular users, filter based on their school (from staff.json)
    if (currentUser?.school) {
      // Map the user's school to the appropriate option
      const schoolMapping = {
        'Boys School': 'boys',
        'Girls School': 'girls', 
        'BaniHajer School': 'banihajer',
        'MOQ School': 'moq'
      };
      
      const userSchoolKey = schoolMapping[currentUser.school];
      if (userSchoolKey) {
        // Return only the user's specific school (no "All Schools" option)
        return allOptions.filter(option => option.value === userSchoolKey);
      }
    }
    
    // If no specific school mapping, return just "all" option as fallback
    return [allOptions[0]]; // Just "All Schools"
  };

  const schoolOptions = getFilteredSchoolOptions();

  // Available modules for security groups - based on Teacher Dashboard modules
  const availableModules = [
    { id: 'overview', name: 'Overview', description: 'Dashboard summary and statistics' },
    { id: 'classes', name: 'My Classes', description: 'Manage your assigned classes' },
    { id: 'monitor', name: 'Live Monitor', description: 'Monitor student screens in real-time' },
    { id: 'assignments', name: 'Assignments', description: 'Create and manage homework' },
    { id: 'analytics', name: 'Analytics', description: 'Performance insights and reports' },
    { id: 'planning', name: 'Planning', description: 'Lesson plans and curriculum' },
    { id: 'design', name: 'Design', description: 'Create educational materials' },
    { id: 'whiteboard', name: 'Whiteboard', description: 'Interactive teaching tools' },
    { id: 'posts', name: 'School Posts', description: 'Manage school announcements' },
    { id: 'chat', name: 'Parent Chat', description: 'Message with parents' },
    { id: 'attendance', name: 'Attendance', description: 'Take attendance for all classes' },
    { id: 'timetable', name: 'My Timetable', description: 'View your personal teaching schedule' },
    { id: 'my-clubs', name: 'My Clubs', description: 'Manage your assigned after-school clubs' },
    { id: 'education-games', name: 'Education Games', description: 'Interactive racing games for students' },
    { id: 'parents-evening', name: 'Parents Evening', description: 'Manage parent-teacher consultations' },
    { id: 'fire-registers', name: 'Fire Registers', description: 'Emergency fire drill attendance' },
    { id: 'fire-marshal', name: 'Fire Marshal', description: 'View fire drill reports and missing students' },
    { id: 'school-manager', name: 'School Manager', description: 'Access to School Manager system' },
    { id: 'hr', name: 'HR', description: 'Human Resources management and staff administration' },
    { id: 'reports', name: 'Reports', description: 'View school analytics and reports - Pupils Overview, Attendance, Applicants, Leavers, Capacity' },
    { id: 'user-management', name: 'User Management', description: 'Manage staff and security groups' },
    { id: 'system-settings', name: 'System Settings', description: 'System configuration and settings' }
  ];

  // Security groups are loaded ONLY from PostgreSQL via loadCustomSecurityGroups()
  // No hardcoded defaults - everything comes from backend

  useEffect(() => {
    initializeData();
    
    // Set initial school selection for non-admin users
    if (!authService.canManageHR() && !currentUser?.hrPermissions?.includes('view-all-schools')) {
      const filteredOptions = getFilteredSchoolOptions();
      if (filteredOptions.length > 0) {
        setSelectedSchool(filteredOptions[0].value);
      }
    }
  }, []);

  const initializeData = async () => {
    try {
      setLoading(true);
      
      // Load staff data
      const staff = await authService.loadStaffAndUsers();
      console.log('📋 UserManagement: Loaded staff data:', staff.length, 'staff members');
      console.log('📋 UserManagement: First 3 staff:', staff.slice(0, 3).map(s => ({ name: s.name, email: s.email })));
      setStaffData(staff);

      // Load security groups from backend first (fetches updated data from PostgreSQL)
      await authService.loadCustomSecurityGroups();
      
      // Initialize security groups - use the new structure (now includes backend updates)
      const storedGroups = authService.getSecurityGroups();
      console.log('🔐 UserManagement: Loaded security groups:', Object.keys(storedGroups));
      console.log('👥 UserManagement: Total members across all groups:', 
        Object.values(storedGroups).reduce((sum, g) => sum + (g.members?.length || 0), 0));
      setSecurityGroups(storedGroups);

      // No need for separate staff assignments - staff are now stored in group members arrays

    } catch (err) {
      setError('Failed to load data: ' + err.message);
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateStaffAssignment = async (email, groupName) => {
    try {
      console.log(`🔄 Updating ${email} → ${groupName}...`);
      
      // Use the new async security groups structure with automatic JSON update
      await authService.updateStaffSecurityGroup(email, groupName);
      // Reload security groups to reflect changes
      const updatedGroups = authService.getSecurityGroups();
      setSecurityGroups(updatedGroups);
      
      console.log(`✅ Assignment updated and saved automatically: ${email} → ${groupName}`);
    } catch (error) {
      console.error('❌ Error updating assignment:', error);
      alert('Error updating assignment. Please try again.');
    }
  };

  const updateStaffSchoolAssignment = async (email, schoolName) => {
    try {
      console.log(`🏫 Updating ${email} school assignment to: ${schoolName}`);
      
      // Use the new automatic school assignment method
      await authService.updateSchoolAssignment(email, schoolName, false);
      
      // Force re-render by updating the state
      setStaffData(prev => [...prev]);
      
      console.log(`✅ School assignment updated and saved automatically: ${email} → ${schoolName}`);
    } catch (error) {
      console.error('❌ Error updating school assignment:', error);
      alert('Error updating school assignment. Please try again.');
    }
  };

  const updateSecurityGroup = async (groupName, updatedGroup) => {
    try {
      console.log(`🔄 Updating security group: ${groupName}`, updatedGroup);
      
      const newGroups = { ...securityGroups };
      // Preserve existing group properties and merge with updates
      newGroups[groupName] = {
        ...securityGroups[groupName],  // Keep name, permissions, members, etc.
        ...updatedGroup                  // Override with edited modules/description
      };
      setSecurityGroups(newGroups);
      authService.setSecurityGroups(newGroups);
      
      // Also save to backend for persistence
      console.log(`📤 Saving security group to backend...`);
      console.log(`📋 Full group data being sent:`, newGroups[groupName]);
      const response = await fetch(buildApiUrl('/api/update-security-groups'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          securityGroups: newGroups
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('✅ Security groups saved to backend:', result);
      } else {
        console.warn('⚠️ Failed to save security groups to backend, but saved to localStorage');
      }
    } catch (error) {
      console.error('❌ Error updating security group:', error);
      alert('Error updating security group: ' + error.message);
    }
  };

  const filteredStaff = staffData.filter(staff => {
    const matchesSearch = staff.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         staff.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         staff.school?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Get the user's security group using the new structure
    const userGroup = authService.getUserSecurityGroup(staff.email);
    // Show ALL staff when no group filter selected, otherwise filter by group (including null/undefined)
    const matchesGroup = !selectedGroup || userGroup === selectedGroup;
    
    // School filtering logic
    const matchesSchool = (() => {
      if (selectedSchool === 'all') return true;
      
      // Show staff without school assignment when filtering by specific school
      if (!staff.school) return true;
      
      // Map staff school to school filter values
      const schoolMapping = {
        'Boys School': 'boys',
        'Girls School': 'girls', 
        'BaniHajer School': 'banihajer',
        'MOQ School': 'moq'
      };
      
      const staffSchoolKey = schoolMapping[staff.school];
      return staffSchoolKey === selectedSchool;
    })();
    
    return matchesSearch && matchesGroup && matchesSchool;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">Loading user management data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
        <p className="font-bold">Error:</p>
        <p>{error}</p>
        <button 
          onClick={initializeData}
          className="mt-2 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-2xl font-bold mb-2">User Management</h2>
        <p className="text-gray-600 mb-4">
          Manage staff access through security groups. Changes are automatically saved and work across all browsers.
        </p>

        {/* Sync and Debug Buttons */}
        <div className="mb-4 flex gap-2">
          <button
            onClick={async () => {
              try {
                await authService.loadUserAssignments();
                const updatedGroups = authService.getSecurityGroups();
                setSecurityGroups(updatedGroups);
                alert('✅ Synced assignments from JSON file!');
              } catch (error) {
                console.error('Sync error:', error);
                alert('❌ Error syncing assignments');
              }
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
          >
            🔄 Sync from JSON
          </button>
          <button
            onClick={() => {
              console.log('🔍 Current localStorage security groups:', authService.getSecurityGroups());
              console.log('🔍 Current JSON assignments:', authService.userAssignments);
              alert('Check console for debugging info');
            }}
            className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors text-sm"
          >
            🔍 Debug Info
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('staff')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'staff'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              👥 Staff Management
            </button>
            <button
              onClick={() => setActiveTab('security-groups')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'security-groups'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              🛡️ Security Groups
            </button>
          </nav>
        </div>
      </div>

      {/* Staff Management Tab */}
      {activeTab === 'staff' && (
        <StaffTab 
          staffData={filteredStaff}
          securityGroups={securityGroups}
          updateStaffAssignment={updateStaffAssignment}
          updateStaffSchoolAssignment={updateStaffSchoolAssignment}
          getSchoolAccessForUser={getSchoolAccessForUser}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          selectedGroup={selectedGroup}
          setSelectedGroup={setSelectedGroup}
          selectedSchool={selectedSchool}
          setSelectedSchool={setSelectedSchool}
          schoolOptions={schoolOptions}
        />
      )}

      {/* Security Groups Tab */}
      {activeTab === 'security-groups' && (
        <SecurityGroupsTab 
          securityGroups={securityGroups}
          updateSecurityGroup={updateSecurityGroup}
          availableModules={availableModules}
          staffData={staffData}
        />
      )}
    </div>
  );
};

// Staff Management Tab Component
const StaffTab = ({ 
  staffData, 
  securityGroups, 
  updateStaffAssignment,
  updateStaffSchoolAssignment,
  getSchoolAccessForUser,
  searchTerm,
  setSearchTerm,
  selectedGroup,
  setSelectedGroup,
  selectedSchool,
  setSelectedSchool,
  schoolOptions
}) => {
  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h3 className="text-xl font-semibold mb-4">Staff Assignment</h3>
      
      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search by name, email, or school..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="w-full md:w-48">
          <select
            value={selectedSchool}
            onChange={(e) => setSelectedSchool(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {schoolOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="w-full md:w-48">
          <select
            value={selectedGroup}
            onChange={(e) => setSelectedGroup(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Groups</option>
            {Object.entries(securityGroups).map(([groupName, group]) => (
              <option key={groupName} value={groupName}>
                {group.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Staff Table - Responsive */}
      <div className="overflow-x-auto">
        {/* Desktop Table View */}
        <table className="w-full table-auto border-collapse hidden md:table">
          <thead>
            <tr className="bg-gray-50">
              <th className="border border-gray-200 px-4 py-3 text-left font-medium">Name</th>
              <th className="border border-gray-200 px-4 py-3 text-left font-medium">Email</th>
              <th className="border border-gray-200 px-4 py-3 text-left font-medium">Original School</th>
              <th className="border border-gray-200 px-4 py-3 text-left font-medium">Assigned School</th>
              <th className="border border-gray-200 px-4 py-3 text-left font-medium">Security Group</th>
              <th className="border border-gray-200 px-4 py-3 text-left font-medium">Modules Access</th>
            </tr>
          </thead>
          <tbody>
            {staffData.map((staff) => {
              const assignedGroupName = authService.getUserSecurityGroup(staff.email);
              const assignedGroup = securityGroups[assignedGroupName];
              const schoolAccess = getSchoolAccessForUser(staff.email);
              
              return (
                <tr key={staff.email} className="hover:bg-gray-50">
                  <td className="border border-gray-200 px-4 py-3">{staff.name}</td>
                  <td className="border border-gray-200 px-4 py-3 text-sm font-mono">{staff.email}</td>
                  <td className="border border-gray-200 px-4 py-3 text-sm text-gray-600">{staff.school || 'Not specified'}</td>
                  <td className="border border-gray-200 px-4 py-3">
                    <select
                      value={schoolAccess?.school || 'none'}
                      onChange={(e) => updateStaffSchoolAssignment(staff.email, e.target.value)}
                      className="px-3 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm w-full"
                    >
                      <option value="none">No Assignment</option>
                      <option value="All Schools">All Schools</option>
                      <option value="Boys School">Boys School</option>
                      <option value="Girls School">Girls School</option>
                      <option value="BaniHajer School">BaniHajer School</option>
                      <option value="MOQ School">MOQ School</option>
                    </select>
                  </td>
                  <td className="border border-gray-200 px-4 py-3">
                    <select
                      value={assignedGroupName}
                      onChange={(e) => updateStaffAssignment(staff.email, e.target.value)}
                      className="px-3 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                    >
                      {Object.entries(securityGroups).map(([groupName, group]) => (
                        <option key={groupName} value={groupName}>
                          {group.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="border border-gray-200 px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {assignedGroup?.modules?.map(moduleId => (
                        <span key={moduleId} className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                          {moduleId}
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Mobile Card View */}
        <div className="md:hidden space-y-4">
          {staffData.map((staff) => {
            const assignedGroupName = authService.getUserSecurityGroup(staff.email);
            const assignedGroup = securityGroups[assignedGroupName];
            const schoolAccess = getSchoolAccessForUser(staff.email);
            
            return (
              <div key={staff.email} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                <div className="mb-3">
                  <h3 className="font-bold text-lg text-gray-900">{staff.name}</h3>
                  <p className="text-sm text-gray-600 font-mono">{staff.email}</p>
                  {!assignedGroupName && (
                    <span className="inline-block mt-1 bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded">
                      ⚠️ No security group assigned
                    </span>
                  )}
                </div>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Original School</label>
                    <p className="text-sm text-gray-600">{staff.school || 'Not specified'}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Assigned School</label>
                    <select
                      value={schoolAccess?.school || 'none'}
                      onChange={(e) => updateStaffSchoolAssignment(staff.email, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    >
                      <option value="none">No Assignment</option>
                      <option value="All Schools">All Schools</option>
                      <option value="Boys School">Boys School</option>
                      <option value="Girls School">Girls School</option>
                      <option value="BaniHajer School">BaniHajer School</option>
                      <option value="MOQ School">MOQ School</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Security Group</label>
                    <select
                      value={assignedGroupName}
                      onChange={(e) => updateStaffAssignment(staff.email, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    >
                      {Object.entries(securityGroups).map(([groupName, group]) => (
                        <option key={groupName} value={groupName}>
                          {group.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Modules Access</label>
                    <div className="flex flex-wrap gap-2">
                      {assignedGroup?.modules?.map(moduleId => (
                        <span key={moduleId} className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                          {moduleId}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {staffData.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No staff members found matching your criteria.
        </div>
      )}
    </div>
  );
};

// Security Groups Tab Component  
const SecurityGroupsTab = ({ 
  securityGroups, 
  updateSecurityGroup, 
  availableModules,
  staffData
}) => {
  const [editingGroup, setEditingGroup] = useState(null);

  const getGroupMemberCount = (groupName) => {
    return securityGroups[groupName]?.members?.length || 0;
  };

  const getGroupMembers = (groupName) => {
    const memberEmails = securityGroups[groupName]?.members || [];
    return staffData.filter(staff => memberEmails.includes(staff.email));
  };

  return (
    <div className="space-y-6">
      {Object.entries(securityGroups).map(([groupName, group]) => (
        <div key={groupName} className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">{group.name}</h3>
                <p className="text-gray-600 mt-1">{group.description}</p>
                <p className="text-sm text-gray-500 mt-2">
                  {getGroupMemberCount(groupName)} members assigned
                </p>
              </div>
              <button
                onClick={() => setEditingGroup(editingGroup === groupName ? null : groupName)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
              >
                {editingGroup === groupName ? 'Close' : 'Edit'}
              </button>
            </div>

            {/* Current Modules */}
            <div className="mb-4">
              <h4 className="font-medium text-gray-900 mb-2">Module Access:</h4>
              <div className="flex flex-wrap gap-2">
                {group.modules?.map(moduleId => {
                  const module = availableModules.find(m => m.id === moduleId);
                  return module ? (
                    <span key={moduleId} className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">
                      {module.name}
                    </span>
                  ) : null;
                })}
              </div>
            </div>

            {/* Edit Mode */}
            {editingGroup === groupName && (
              <SecurityGroupEditor
                group={group}
                availableModules={availableModules}
                onSave={(updatedGroup) => {
                  updateSecurityGroup(groupName, updatedGroup);
                  setEditingGroup(null);
                }}
                onCancel={() => setEditingGroup(null)}
              />
            )}

            {/* Group Members */}
            <div className="border-t border-gray-200 pt-4">
              <h4 className="font-medium text-gray-900 mb-2">Members:</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-40 overflow-y-auto">
                {getGroupMembers(groupName).map(member => (
                  <div key={member.email} className="bg-gray-50 rounded px-3 py-2">
                    <div className="font-medium text-sm">{member.name}</div>
                    <div className="text-xs text-gray-600">{member.email}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Security Group Editor Component
const SecurityGroupEditor = ({ group, availableModules, onSave, onCancel }) => {
  const [editedGroup, setEditedGroup] = useState({ ...group });

  const handleModuleToggle = (moduleId) => {
    const newModules = editedGroup.modules.includes(moduleId)
      ? editedGroup.modules.filter(id => id !== moduleId)
      : [...editedGroup.modules, moduleId];
    
    setEditedGroup({ ...editedGroup, modules: newModules });
  };

  return (
    <div className="border-t border-gray-200 pt-4 mt-4">
      <h4 className="font-medium text-gray-900 mb-3">Edit Security Group</h4>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description:
          </label>
          <input
            type="text"
            value={editedGroup.description}
            onChange={(e) => setEditedGroup({ ...editedGroup, description: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Module Access:
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {availableModules.map(module => (
              <label key={module.id} className="flex items-start space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={editedGroup.modules.includes(module.id)}
                  onChange={() => handleModuleToggle(module.id)}
                  className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <div>
                  <div className="font-medium">{module.name}</div>
                  <div className="text-sm text-gray-600">{module.description}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(editedGroup)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;